import { useEffect, useRef, useState } from "react";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import { useCollection } from "../../../../firebase-files/hooks/useCollection";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import "./PlantationTwoAboutUs.scss";

const PlantationTwoAboutUs = () => {
  const [aboutUsHtml, setAboutUsHtml] = useState("");
  const [counterData, setCounterData] = useState([]);
  const [faviconUrl, setFaviconUrl] = useState("");
  const [aboutUsImageUrl, setAboutUsImageUrl] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const counterRefs = useRef([]);
  const sectionRef = useRef(null);
  const [apptype, setAppType] = useState("");

  const { user } = useAuthContext();
  const { document: organisationData, error: aboutUsError } = useDocument(
    "settings",
    "organisation"
  );
  const { document: homeCounter, error: counterError } = useDocument(
    "counters",
    "homeCounter"
  );

  const { document: appTypeDoc } = useDocument("settings", "AppType");

  // ✅ users collection fetch
  const { documents: users } = useCollection("users");
  const { updateDocument } = useFirestore("settings");

  // ✅ check if current user is admin or superAdmin
  useEffect(() => {
    if (!user || !users) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    const phone = user?.phoneNumber?.replace("+91", "91");
    const userInfo = users.find((u) => u.id === phone);

    if (userInfo) {
      setIsAdmin(userInfo.role?.toLowerCase() === "admin");
      setIsSuperAdmin(userInfo.role?.toLowerCase() === "superadmin");
    } else {
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  }, [user, users]);

  // ✅ load organisation data and app type
  useEffect(() => {
    if (organisationData) {
      setAboutUsHtml(
        organisationData.aboutUs ||
        "<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>"
      );
      setFaviconUrl(
        organisationData.FavIcon ||
        "https://i.pinimg.com/736x/bf/e0/a1/bfe0a181c4f69de2ac6a4d4c5861b4b4.jpg"
      );
      setAboutUsImageUrl(
        organisationData.imageUrl ||
        "https://via.placeholder.com/300x300?text=About+Us+Image"
      );
      
      // Set app type from organisationData if available
      if (organisationData.apptype) {
        setAppType(organisationData.apptype);
      }
    } else if (aboutUsError) {
      setAboutUsHtml("<p>Error loading content</p>");
    }
  }, [organisationData, aboutUsError]);

  // ✅ load app type from appTypeDoc if available
  useEffect(() => {
    if (appTypeDoc && appTypeDoc.apptype) {
      setAppType(appTypeDoc.apptype);
    }
  }, [appTypeDoc]);

  // ✅ load counters
  useEffect(() => {
    if (homeCounter) {
      const extractedData = [
        homeCounter.firstCounter,
        homeCounter.secondCounter,
        homeCounter.thirdCounter,
        homeCounter.fourthCounter,
      ].filter((counter) => counter && counter.name && counter.number);
      setCounterData(extractedData);
    } else if (counterError) {
      setCounterData([]);
    }
  }, [homeCounter, counterError]);

  // ✅ animate counters
  useEffect(() => {
    if (counterData.length === 0) return;

    const animateCounters = () => {
      counterRefs.current.forEach((counter, index) => {
        if (!counter || index >= counterData.length) return;

        const targetString = counterData[index].number.toString();
        const numericValue = parseFloat(targetString.replace(/[^0-9.]/g, ""));
        const hasPlus = targetString.endsWith("+");
        const hasPercent = targetString.endsWith("%");

        const duration = 2000;
        const start = performance.now();

        const update = (time) => {
          const elapsed = time - start;
          const progress = Math.min(elapsed / duration, 1);
          const value = Math.floor(progress * numericValue);
          counter.innerHTML = hasPlus
            ? `${value}+`
            : hasPercent
              ? `${value}%`
              : value.toLocaleString();
          if (progress < 1) requestAnimationFrame(update);
        };

        requestAnimationFrame(update);
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) animateCounters();
        });
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, [counterData]);

  const handleAboutUsImageUpload = async (e) => {
    const canUpload = (apptype === "lite" && isSuperAdmin) || 
                     (apptype === "pro" && (isSuperAdmin || isAdmin));
    
    if (!canUpload) {
      alert("You don't have permission to upload images!");
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      alert("Please select an image file!");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64String = event.target.result;
      try {
        await updateDocument("organisation", { imageUrl: base64String });
        setAboutUsImageUrl(base64String);
      } catch (error) {
        console.error("Image upload failed:", error);
        alert("Image upload failed. Please try again.");
      }
    };
    reader.onerror = () => {
      console.error("File reading error");
      alert("Error reading file. Please try again.");
    };
    reader.readAsDataURL(file);
  };

  const showUploadButton = () => {
    if (apptype === "lite" && isSuperAdmin) return true;
    if (apptype === "pro" && (isSuperAdmin || isAdmin)) return true;
    return false;
  };

  return (
    <div className="plantation-two-about-us" ref={sectionRef}>
      <div className="seprator-container">
        {/* ✅ About Us Image */}
        <div className="round-image-container">
          <img src={aboutUsImageUrl} className="round-image" alt="About Us" />

          {showUploadButton() && (
            <div className="upload-btn-wrapper">
              <label htmlFor="upload-aboutus-image" className="upload-label">
                Add Image
              </label>
              <input
                id="upload-aboutus-image"
                type="file"
                accept="image/*"
                onChange={handleAboutUsImageUpload}
                className="upload-input"
              />
            </div>
          )}
        </div>

        <div className="content-right">
          <h1>About Us</h1>
          {aboutUsError && <div className="error">Error loading About Us</div>}
          <div
            className="about-us-text"
            dangerouslySetInnerHTML={{ __html: aboutUsHtml }}
          />
          {counterError && (
            <div className="error">Error loading counters: {counterError}</div>
          )}
          {!homeCounter && !counterError && (
            <div className="loading">Loading counters...</div>
          )}

          {homeCounter && (
            <div className="stats-grid">
              {counterData.map((item, index) => (
                <div className="stat-item" key={index}>
                  <div
                    className="stat-icon"
                    dangerouslySetInnerHTML={{ __html: item.iconName }}
                  />
                  <h3>
                    <span ref={(el) => (counterRefs.current[index] = el)}>
                      {item.number.toString().endsWith("+")
                        ? "0+"
                        : item.number.toString().endsWith("%")
                          ? "0%"
                          : "0"}
                    </span>
                  </h3>
                  <p>{item.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlantationTwoAboutUs;